/* Signup and verification — STORY-001.
 *
 * One definition, used by the API to validate and by the clients to check
 * before submitting. The client-side check is a convenience for the person
 * typing; the server validates independently and is the only thing trusted
 * (`frontend-react.md`).
 */
import { z } from 'zod';
/** The whole password policy. Decided 2026-08-14 — STORY-001 records what this
 *  deliberately accepts, including that `12345678` is a valid password. */
export const PASSWORD_MIN_LENGTH = 8;
/* Long enough for the longest real addresses, short enough that nobody posts a
 * megabyte to an unauthenticated endpoint. Bytes are capped by the body limit;
 * this is about the field. */
const EMAIL_MAX_LENGTH = 254;
const DISPLAY_NAME_MAX_LENGTH = 120;
export const signUpRequestSchema = z.object({
    email: z.string().trim().min(1).max(EMAIL_MAX_LENGTH).pipe(z.email()),
    /* Not trimmed, and that is deliberate: a leading or trailing space is part of
     * the password somebody chose, and silently removing it means they cannot
     * sign in with what they typed. Length is counted in code points, so an
     * eight-emoji password passes — which is fine, it is more entropy than
     * `password`. */
    password: z.string().min(PASSWORD_MIN_LENGTH),
    displayName: z.string().trim().min(1).max(DISPLAY_NAME_MAX_LENGTH),
    /* Both are required and must be true. A single "I accept" covering both
     * documents would make the record ambiguous about which was agreed, and the
     * two are versioned independently. */
    acceptsTerms: z.literal(true),
    acceptsDpa: z.literal(true),
});
/* Deliberately says nothing about whether the address was already registered.
 * The response to a duplicate is byte-for-byte this one — STORY-001, no
 * enumeration. It carries no user id for the same reason. */
export const signUpResponseSchema = z.object({
    status: z.literal('verification_sent'),
});
export const signInRequestSchema = z.object({
    email: z.string().trim().min(1).max(EMAIL_MAX_LENGTH),
    /* No minimum here, deliberately. Enforcing the signup policy on sign-in
     * would refuse a short password locally and tell an attacker that eight
     * characters is the rule before they have tried anything — and it would lock
     * out anyone whose password predates a future policy change. The server
     * checks the hash; length is a signup concern. */
    password: z.string().min(1),
});
/** One Organisation the signed-in User belongs to. May be an empty list. */
export const membershipSchema = z.object({
    organisationId: z.string(),
    name: z.string(),
    role: z.enum(['owner', 'member']),
});
/* What the client needs to decide where to send somebody, and nothing more.
 *
 * `memberships` can be empty, and that is a normal state rather than an error:
 * signup creates a User with none (ADR-0010), so a first sign-in lands on the
 * create-or-join fork rather than a dashboard. STORY-002's "they land on their
 * Organisation's dashboard" is the case where the list has entries. */
export const currentUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
    locale: z.string(),
    emailVerified: z.boolean(),
    memberships: z.array(membershipSchema),
});
export const signInResponseSchema = z.object({
    user: currentUserSchema,
});
export const verifyEmailRequestSchema = z.object({
    token: z.string().min(1),
});
export const verifyEmailResponseSchema = z.object({
    /* `already_verified` is a success, not an error: clicking the link twice, or
     * a mail client prefetching it, must not look like a failure to the person. */
    status: z.enum(['verified', 'already_verified']),
});
/* ── password reset — STORY-003 ─────────────────────────────────────────────── */
export const requestPasswordResetRequestSchema = z.object({
    email: z.string().trim().min(1).max(EMAIL_MAX_LENGTH),
});
/* Says nothing about whether an account exists. The response to an unknown
 * address is byte-for-byte this one — that is the whole point of the endpoint's
 * design, and a field here reporting "sent: true" would undo it. */
export const requestPasswordResetResponseSchema = z.object({
    status: z.literal('accepted'),
});
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    /* Same policy as signup, and shared from the same constant so the two cannot
     * drift into disagreeing about what a valid password is. */
    password: z.string().min(PASSWORD_MIN_LENGTH),
});
export const resetPasswordResponseSchema = z.object({
    status: z.literal('reset'),
    /* Reported so the screen can say it out loud. Somebody resetting because they
     * suspect a compromise needs to know the other sessions are gone, and a
     * silent security action is one nobody trusts happened. */
    sessionsEnded: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.js.map